import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const EMPTY = {
  facility_id: "", facility_type: "Working Capital", product_type: "Revolving Credit",
  borrower_id: "", currency: "IDR", approved_limit: 0, utilized_limit: 0,
  blocked_amount: 0, interest_rate: 10, start_date: "", maturity_date: "",
  status: "Active", purpose: "", allow_excess: false, excess_reason: "",
};

export default function FacilityFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [borrowers, setBorrowers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/borrowers").then((r) => setBorrowers(r.data));
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
    }
  }, [open, initial]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async () => {
    if (!form.facility_id || !form.borrower_id) {
      toast.error("Facility ID dan Borrower wajib diisi");
      return;
    }
    const approved = parseFloat(form.approved_limit) || 0;
    const utilized = parseFloat(form.utilized_limit) || 0;
    const blocked = parseFloat(form.blocked_amount) || 0;
    if (utilized + blocked > approved) {
      if (!form.allow_excess) {
        toast.error(`Utilized + blocked (${(utilized + blocked).toLocaleString("id-ID")}) melebihi approved limit (${approved.toLocaleString("id-ID")}). Centang "Izinkan Excess" + isi alasan jika ini disengaja.`);
        return;
      }
      if (!(form.excess_reason || "").trim()) {
        toast.error("Isi 'Alasan Excess' untuk audit trail.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        approved_limit: approved,
        utilized_limit: utilized,
        blocked_amount: blocked,
        interest_rate: parseFloat(form.interest_rate) || 0,
        allow_excess: !!form.allow_excess,
        excess_reason: form.excess_reason || "",
      };
      if (initial) {
        await api.put(`/facilities/${initial.facility_id}`, payload);
        toast.success("Facility diupdate");
      } else {
        await api.post("/facilities", payload);
        toast.success("Facility dibuat");
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="facility-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit Fasilitas" : "Tambah Fasilitas"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Facility ID" required>
            <Input value={form.facility_id} disabled={!!initial} onChange={(e) => set("facility_id", e.target.value)} className="mono" data-testid="facility-id-input" />
          </Field>
          <Field label="Borrower" required>
            <Select value={form.borrower_id} onValueChange={(v) => set("borrower_id", v)}>
              <SelectTrigger><SelectValue placeholder="Pilih borrower" /></SelectTrigger>
              <SelectContent>
                {borrowers.map((b) => <SelectItem key={b.borrower_id} value={b.borrower_id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tipe Facility">
            <Select value={form.facility_type} onValueChange={(v) => set("facility_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Working Capital", "Term Loan", "Trade Finance", "Overdraft", "Bank Guarantee"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tipe Produk">
            <Input value={form.product_type} onChange={(e) => set("product_type", e.target.value)} />
          </Field>
          <Field label="Approved Limit (IDR)">
            <Input type="number" value={form.approved_limit} onChange={(e) => set("approved_limit", e.target.value)} className="mono" data-testid="facility-approved-input" />
          </Field>
          <Field label="Utilized Limit (IDR)">
            <Input type="number" value={form.utilized_limit} onChange={(e) => set("utilized_limit", e.target.value)} className="mono" />
          </Field>
          <Field label="Blocked Amount (IDR)">
            <Input type="number" value={form.blocked_amount} onChange={(e) => set("blocked_amount", e.target.value)} className="mono" />
          </Field>
          <Field label="Interest Rate (%)">
            <Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} className="mono" />
          </Field>
          <Field label="Start Date">
            <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          </Field>
          <Field label="Maturity Date">
            <Input type="date" value={form.maturity_date} onChange={(e) => set("maturity_date", e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
                <SelectItem value="Suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Currency">
            <Input value={form.currency} onChange={(e) => set("currency", e.target.value)} className="mono" />
          </Field>
          <Field label="Tujuan" className="col-span-2">
            <Textarea value={form.purpose} onChange={(e) => set("purpose", e.target.value)} rows={2} />
          </Field>

          {/* Excess (over-limit) acknowledgment */}
          {(() => {
            const approved = parseFloat(form.approved_limit) || 0;
            const utilized = parseFloat(form.utilized_limit) || 0;
            const blocked = parseFloat(form.blocked_amount) || 0;
            const over = (utilized + blocked) > approved;
            const overBy = (utilized + blocked) - approved;
            return (
              <div className={`col-span-2 border rounded-md p-3 ${over ? "border-[#D0021B] bg-red-50" : "border-border"}`}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.allow_excess}
                    onChange={(e) => set("allow_excess", e.target.checked)}
                    className="mt-1"
                    data-testid="facility-allow-excess"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">
                      Izinkan Excess (utilized + blocked &gt; approved)
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Default: server akan TOLAK update jika outstanding melebihi approved limit.
                      Aktifkan hanya untuk excess yang sah (bunga akrual, FX revaluation, fee bulanan).
                    </div>
                    {over && (
                      <div className="text-xs font-bold text-[#D0021B] mt-1 mono">
                        ⚠ Saat ini over-limit sebesar Rp {Math.round(overBy).toLocaleString("id-ID")}
                      </div>
                    )}
                  </div>
                </label>
                {form.allow_excess && (
                  <div className="mt-2 pl-6">
                    <Label className="text-xs">Alasan Excess (wajib, untuk audit trail)</Label>
                    <Input
                      value={form.excess_reason || ""}
                      onChange={(e) => set("excess_reason", e.target.value)}
                      placeholder="Contoh: Bunga akrual & fee akhir bulan"
                      data-testid="facility-excess-reason"
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="facility-submit-button">
            {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children, className }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}{required && <span className="text-[#D0021B]"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
